import { Modal, Progress, Transition } from '@mantine/core';
import { useUnit } from 'effector-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
	LOADER_WIDTH_ASPECT,
	LOADER_WIDTH,
	LOADER_HEIGHT_ASPECT,
	LOADER_HEIGHT,
} from 'widgets/loader/config/loader-config';
import { IS_STEPBALL } from 'shared/config';
import { isDesktop, useBreakpoint } from 'shared/lib';
import { Button } from 'shared/ui';
import ChezachiniLogo from 'shared/ui/assets/icons/chezachini-logo.svg?react';
import LeprecoinsLogo from 'shared/ui/assets/icons/leprecoins-logo.svg?react';
import StepballLogo from 'shared/ui/assets/icons/stepball-logo.svg?react';
import { LOADER_TRANSITION_DURATION } from '../../config';
import { loaderModel, LOADER_STAGES } from '../../model';
import cls from './loader.module.scss';

const GameLogo = IS_STEPBALL ? StepballLogo : ChezachiniLogo;

export const Loader = () => {
	const { t } = useTranslation();
	const toggler = useUnit(loaderModel.toggler);
	const rootRef = useRef<HTMLDivElement>(null);

	const [stage, progress, mounted] = useUnit([
		loaderModel.$stage,
		loaderModel.$progress,
		loaderModel.mounted,
	]);

	const sm = useBreakpoint('sm');

	useEffect(() => {
		const handleResize = () => {
			setTimeout(() => {
				if (!toggler.active) return;

				if (rootRef.current === null) return;

				const width = document.documentElement.clientWidth;
				const height = document.documentElement.clientHeight;

				if (isDesktop(width, height)) {
					const scaleX = (LOADER_WIDTH_ASPECT * width) / LOADER_WIDTH;
					const scaleY = (LOADER_HEIGHT_ASPECT * height) / LOADER_HEIGHT;

					rootRef.current.style.setProperty('--scale', `${(scaleX + scaleY) / 2}`);

					return;
				}

				rootRef.current.style.setProperty('--scale', `1`);
			}, 2);
		};

		handleResize();

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [toggler.active]);

	useEffect(() => {
		mounted();
	}, []);

	return (
		<Modal
			ref={rootRef}
			centered
			fullScreen
			data-exiting={!toggler.active || undefined}
			opened={toggler.active}
			variant='transparent'
			zIndex={1000}
			classNames={{
				root: cls.loaderRoot,
				overlay: cls.loaderOverlay,
				inner: cls.loaderInner,
				content: cls.loaderContent,
			}}
			withCloseButton={false}
			closeOnEscape={false}
			closeOnClickOutside={false}
			onClose={toggler.disable}
		>
			<Transition
				mounted={stage === LOADER_STAGES.logo}
				transition='fade'
				duration={LOADER_TRANSITION_DURATION}
			>
				{(styles) => (
					<LeprecoinsLogo
						style={styles}
						className={cls.loaderLeprecoins}
					/>
				)}
			</Transition>
			<Transition
				mounted={stage !== LOADER_STAGES.init && stage !== LOADER_STAGES.logo}
				transition='fade'
				duration={LOADER_TRANSITION_DURATION}
				enterDelay={LOADER_TRANSITION_DURATION}
			>
				{(styles) => (
					<GameLogo
						style={styles}
						className={cls.loaderGame}
					/>
				)}
			</Transition>
			<Transition
				mounted={stage === LOADER_STAGES.progress || stage === LOADER_STAGES.progressFulfill}
				transition='fade'
				duration={LOADER_TRANSITION_DURATION}
				enterDelay={LOADER_TRANSITION_DURATION}
			>
				{(styles) => (
					<Progress
						size={sm ? 'md' : 'sm'}
						value={progress}
						style={styles}
						className={cls.loaderProgress}
					/>
				)}
			</Transition>
			<Transition
				mounted={stage === LOADER_STAGES.play}
				transition='fade'
				duration={LOADER_TRANSITION_DURATION}
				enterDelay={LOADER_TRANSITION_DURATION}
			>
				{(styles) => (
					<Button
						style={styles}
						className={cls.loaderPlay}
						onClick={() => {
							toggler.disable();
						}}
					>
						{t('loader.play-button-label')}
					</Button>
				)}
			</Transition>
		</Modal>
	);
};
